// Export utilities for PDF and CSV/Excel export

/**
 * Export data to CSV format
 */
export function exportToCSV(data: any[], filename: string) {
  if (!data || data.length === 0) {
    alert("No data to export");
    return;
  }

  // Get headers from first object
  const headers = Object.keys(data[0]);

  // Create CSV content
  const csvContent = [
    headers.join(","), // Header row
    ...data.map((row) =>
      headers
        .map((header) => {
          const value = row[header];
          // Handle values with commas or quotes
          if (typeof value === "string" && (value.includes(",") || value.includes('"'))) {
            return `"${value.replace(/"/g, '""')}"`;
          }
          return value ?? "";
        })
        .join(",")
    ),
  ].join("\n");

  // Create blob and download
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);

  link.setAttribute("href", url);
  link.setAttribute("download", `${filename}.csv`);
  link.style.visibility = "hidden";

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * Export evaluation report to PDF using browser print
 */
export function exportToPDF(title: string) {
  // Store original title
  const originalTitle = document.title;
  document.title = title;

  // Trigger browser print dialog
  window.print();

  // Restore original title
  setTimeout(() => {
    document.title = originalTitle;
  }, 100);
}

/**
 * Prepare evaluation data for CSV export
 */
export function prepareEvaluationDataForExport(evaluations: any[]) {
  return evaluations.map((evaluation) => ({
    ID: evaluation.id,
    "User ID": evaluation.userId,
    "Evaluator ID": evaluation.evaluatorId,
    Period: evaluation.periodId || evaluation.period,
    Type: evaluation.evaluationType,
    Status: evaluation.status,
    "Overall Score": evaluation.overallScore ?? "N/A",
    Comments: evaluation.comments ? evaluation.comments.substring(0, 100) : "",
    Created: new Date(evaluation.createdAt).toLocaleString(),
    Submitted: evaluation.submittedAt ? new Date(evaluation.submittedAt).toLocaleString() : "N/A",
  }));
}

/**
 * Prepare team evaluation data for CSV export
 */
export function prepareTeamEvaluationDataForExport(
  evaluations: any[],
  teamsMap: Record<string, string>
) {
  return evaluations.map((evaluation) => ({
    ID: evaluation.id,
    Team: teamsMap[evaluation.teamId] || evaluation.teamId,
    Period: evaluation.periodId,
    "Evaluator ID": evaluation.evaluatorId,
    Status: evaluation.status,
    "Team Score": evaluation.overallScore ?? "N/A",
    "Individual Avg": evaluation.individualAvgScore ?? "N/A",
    Difference:
      evaluation.overallScore && evaluation.individualAvgScore
        ? (evaluation.overallScore - evaluation.individualAvgScore).toFixed(2)
        : "N/A",
    Comments: evaluation.comments ? evaluation.comments.substring(0, 100) : "",
    Created: new Date(evaluation.createdAt).toLocaleString(),
  }));
}

/**
 * Prepare detailed evaluation report data for CSV export
 */
export function prepareDetailedEvaluationForExport(
  evaluation: any,
  criteria: any[],
  type: "individual" | "team"
) {
  const rows: any[] = [];

  // Summary row
  rows.push({
    Section: "Summary",
    Field: type === "team" ? "Team" : "User ID",
    Value: type === "team" ? evaluation.teamId : evaluation.userId,
  });

  rows.push({
    Section: "Summary",
    Field: "Period",
    Value: evaluation.periodId,
  });

  rows.push({
    Section: "Summary",
    Field: "Status",
    Value: evaluation.status,
  });

  if (type === "individual") {
    rows.push({
      Section: "Summary",
      Field: "Evaluation Type",
      Value: evaluation.evaluationType,
    });
  }

  rows.push({
    Section: "Summary",
    Field: "Overall Score",
    Value: evaluation.overallScore ?? "N/A",
  });

  if (type === "team" && evaluation.individualAvgScore) {
    rows.push({
      Section: "Summary",
      Field: "Individual Average",
      Value: evaluation.individualAvgScore,
    });
  }

  // Scores by criteria
  const criteriaMap = new Map(criteria.map((c) => [c.id, c]));
  Object.entries(evaluation.scores || {}).forEach(([criteriaId, score]) => {
    const criterion = criteriaMap.get(criteriaId);
    rows.push({
      Section: "Scores",
      Field: criterion?.name || criteriaId,
      Value: score,
    });
  });

  // Comments
  if (evaluation.comments) {
    rows.push({
      Section: "Comments",
      Field: "Feedback",
      Value: evaluation.comments,
    });
  }

  return rows;
}
